import { z } from 'zod';

/**
 * Avaliacao — bilateral post-evento ratings (Phase 13 v1.2).
 *
 * Single source of truth shared by backend DTOs (createZodDto) and frontend
 * api-client. Framework-agnostic — duplicates enum literals (does NOT import
 * @prisma/client).
 *
 * Papel mirrors Prisma `AvaliacaoPapel`:
 *   - 'profissional' → empresa avalia profissional aprovado (avaliado = User do prof)
 *   - 'empresa'      → profissional aprovado avalia empresa (avaliado = User admin)
 *
 * Visibility (comentarioVisivel): true when BOTH parties submitted OR when the
 * 7-day window after evento.dataFim has expired. Computed server-side at read time.
 */

// ---------- Tags (Phase 29 DEBT-01) ----------
//
// Closed whitelist — 15 canonical tags deduplicated across both papel directions:
//   empresa-avalia-profissional (8): Pontual … Proativo
//   profissional-avalia-empresa (8): Pagamento pontual … Indica para outros
//   'Organizado' appears in both lists; deduplicated to single enum member.
//
// Any value outside this enum is rejected 400 at the DTO layer (Zod whitelist).
// Source of truth: 29-UI-SPEC.md §Tag whitelist.
export const AvaliacaoTagSchema = z.enum([
  // empresa-avalia-profissional
  'Pontual',
  'Comunicativo',
  'Profissional',
  'Organizado',
  'Dedicado',
  'Qualificado',
  'Liderança',
  'Proativo',
  // profissional-avalia-empresa
  'Pagamento pontual',
  'Comunicação clara',
  'Ambiente seguro',
  'Material disponível',
  'Equipe respeitosa',
  'Retorno rápido',
  'Indica para outros',
]);

// ---------- Papel ----------

export const AvaliacaoPapelSchema = z.enum(['profissional', 'empresa']);

// ---------- Inputs ----------

export const SubmeterAvaliacaoSchema = z.object({
  eventoId: z.string().min(1),
  avaliadoId: z.string().min(1),
  papel: AvaliacaoPapelSchema,
  // z.coerce.number(): allow string inputs from form submissions; clamp 1..5.
  // DB CHECK + Zod parse give defense-in-depth.
  estrelas: z.coerce.number().int().min(1).max(5),
  comentario: z.string().max(500).trim().optional(),
  // Phase 29 DEBT-01: multi-select tags required (min 1) per REQUIREMENTS.md.
  // Closed whitelist via AvaliacaoTagSchema — unknown values rejected 400.
  tags: z
    .array(AvaliacaoTagSchema)
    .min(1, 'Selecione ao menos 1 tag para enviar sua avaliação')
    .max(15)                          // hard ceiling = total distinct enum values
    .refine(
      (arr) => new Set(arr).size === arr.length,
      { message: 'Tags duplicadas não são permitidas' },
    ),
});

// ---------- DTOs ----------

export const AvaliacaoDtoSchema = z.object({
  id: z.string(),
  eventoId: z.string(),
  avaliadorId: z.string(),
  avaliadoId: z.string(),
  papel: AvaliacaoPapelSchema,
  estrelas: z.number().int(),
  comentario: z.string().nullable(),
  // Computed: true when counter-rating exists OR window expired (>7d after dataFim).
  comentarioVisivel: z.boolean(),
  criadaEm: z.string(),
  // Phase 29 DEBT-01: tags array in DTO (may be empty for rows created before migration).
  tags: z.array(AvaliacaoTagSchema),
});

// ---------- Reputation aggregate (RATE-03 public) ----------

export const ReputationCommentSchema = z.object({
  estrelas: z.number().int(),
  comentario: z.string(),
  criadaEm: z.string(),
  eventoNome: z.string(),
  // Phase 29 gap: tags carried through for display in reputation views.
  // Optional to remain backward-compatible with rows created before Phase 29.
  tags: z.array(AvaliacaoTagSchema).optional(),
});

export const ReputationResponseSchema = z.object({
  // 1-decimal media; null when total = 0 (fallback "Sem avaliações ainda").
  media: z.number().nullable(),
  total: z.number().int(),
  ultimos3Comentarios: z.array(ReputationCommentSchema),
});

// ---------- Eligibility (RATE-01 modal gate) ----------

export const EligibilityTargetSchema = z.object({
  avaliadoId: z.string(),
  nome: z.string(),
  papel: AvaliacaoPapelSchema,
  alreadyRated: z.boolean(),
  avatarUrl: z.string().nullable().optional(),
});

export const EligibilityResponseSchema = z.object({
  canRate: z.boolean(),
  role: z.enum(['empresa', 'profissional']).nullable(),
  targets: z.array(EligibilityTargetSchema),
  // ISO string of (evento.dataFim + 7 days); null when evento is not finalizado.
  windowClosesAt: z.string().nullable(),
  eventoNome: z.string(),
  eventoStatus: z.string(),
});

// ---------- listMine (compact alreadyRated map for modal trigger) ----------

export const MyAvaliacaoEntrySchema = z.object({
  avaliadoId: z.string(),
  papel: AvaliacaoPapelSchema,
  alreadyRated: z.boolean(),
});

export const MyAvaliacoesResponseSchema = z.object({
  eventoId: z.string(),
  entries: z.array(MyAvaliacaoEntrySchema),
});

// ---------- Inferred types ----------

export type AvaliacaoTag = z.infer<typeof AvaliacaoTagSchema>;
export type AvaliacaoPapel = z.infer<typeof AvaliacaoPapelSchema>;
export type SubmeterAvaliacaoInput = z.infer<typeof SubmeterAvaliacaoSchema>;
export type AvaliacaoDto = z.infer<typeof AvaliacaoDtoSchema>;
export type ReputationComment = z.infer<typeof ReputationCommentSchema>;
export type ReputationResponse = z.infer<typeof ReputationResponseSchema>;
export type EligibilityTarget = z.infer<typeof EligibilityTargetSchema>;
export type EligibilityResponse = z.infer<typeof EligibilityResponseSchema>;
export type MyAvaliacaoEntry = z.infer<typeof MyAvaliacaoEntrySchema>;
export type MyAvaliacoesResponse = z.infer<typeof MyAvaliacoesResponseSchema>;
